import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

// What Prism records for an option of a marked list: the short code in brackets if the label ends with one
// ("Remote Patient Monitoring (RPM)" -> "RPM"), else the label exactly as shown. Never re-cased: Prism's name
// redaction relies on capital letters, so changing them could let a name through.
export function prismOptionName(label) {
  const text = String(label ?? "").trim();
  const code = text.match(/\(([A-Z]{2,6})\)\s*$/);
  return code ? code[1] : text;
}

function renderField(field, value, handleChange, formType) {
  const fieldTestId = `generic-form-${field.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`;
  const commonProps = {
    fullWidth: true,
    label: field.label,
    margin: "normal",
    required: field.required,
    "data-testid": fieldTestId,
    "data-field-name": field.name,
    "data-field-type": field.type,
    "data-field-category": formType.toLowerCase(),
    "data-form-type": formType,
  };

  switch (field.type) {
    case "select": {
      const labelId = `${fieldTestId}-label`;
      return (
        <FormControl fullWidth margin="normal" key={field.name}>
          <InputLabel id={labelId}>{field.label}</InputLabel>
          <Select
            labelId={labelId}
            id={`${fieldTestId}-select`}
            label={field.label}
            value={value || ""}
            onChange={(e) => handleChange(field.name, e.target.value)}
            required={field.required}
            data-testid={`${fieldTestId}-select`}
            data-field-name={field.name}
            data-field-type={field.type}
            data-field-category={formType.toLowerCase()}
            data-form-type={formType}
            // A fixed list that isn't patient data (program, reading type...): Prism may record the option picked.
            // The mark is on the option list itself, so Prism never mistakes another list for this one.
            // Never set this on a list built from a patient's records or on clinical/personal fields.
            MenuProps={field.recordChoice ? { MenuListProps: { "data-prism-choice": field.recordChoice } } : undefined}
          >
            {field.options.map((option, index) =>
              typeof option === "object" && option.value !== undefined ? (
                <MenuItem 
                  key={option.value} 
                  value={option.value}
                  data-testid={`${fieldTestId}-option-${index}`}
                  data-prism-option={field.recordChoice ? prismOptionName(option.label) : undefined}
                >
                  {option.label}
                </MenuItem>
              ) : (
                <MenuItem 
                  key={option} 
                  value={option}
                  data-testid={`${fieldTestId}-option-${index}`}
                  data-prism-option={field.recordChoice ? prismOptionName(option) : undefined}
                >
                  {option}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>
      );
    }
    case "textarea":
      return (
        <TextField
          key={field.name}
          {...commonProps}
          multiline
          rows={3}
          value={value || ""}
          onChange={(e) => handleChange(field.name, e.target.value)}
          data-testid={`${fieldTestId}-textarea`}
          data-field-name={field.name}
          data-field-type={field.type}
          data-field-category={formType.toLowerCase()}
          data-form-type={formType}
        />
      );
    case "checkbox":
      return (
        <FormControlLabel
          key={field.name}
          control={
            <Checkbox
              checked={Boolean(value)}
              onChange={(e) => handleChange(field.name, e.target.checked)}
              data-testid={`${fieldTestId}-checkbox`}
              data-field-name={field.name}
              data-field-type={field.type}
              data-field-category={formType.toLowerCase()}
              data-form-type={formType}
              aria-label={field.label}
            />
          }
          label={field.label}
          sx={{ mt: 2, mb: 1 }}
        />
      );
    default: {
      const input = (
        <TextField
          key={field.name}
          {...commonProps}
          type={field.type}
          value={value ?? ""}
          inputProps={field.inputProps}
          onChange={(e) => handleChange(field.name, e.target.value)}
          InputLabelProps={field.type.includes("date") ? { shrink: true } : {}}
          data-testid={`${fieldTestId}-input`}
          data-field-name={field.name}
          data-field-type={field.type}
          data-field-category={formType.toLowerCase()}
          data-form-type={formType}
        />
      );
      if (!field.fill) return input;
      // A shortcut that fills the field ("Use my name"). A broken one is a deliberate demo UI issue: it does nothing.
      return (
        <Box key={field.name}>
          {input}
          <Button
            size="small"
            sx={{ mt: -1 }}
            onClick={() => { if (!field.fill.broken) handleChange(field.name, field.fill.value); }}
            data-testid={`${fieldTestId}-fill`}
          >
            {field.fill.label}
          </Button>
        </Box>
      );
    }
  }
}

function GenericFormDialog({
  open,
  title,
  fields,
  formData,
  onChange,
  onCancel,
  onSubmit,
  submitLabel,
  formType,
}) {
  const handleFieldChange = (name, newValue) => {
    onChange({
      ...formData,
      [name]: newValue,
    });
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {fields.map((field) => renderField(field, formData[field.name], handleFieldChange, formType))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onCancel}
          data-testid="generic-form-cancel-button"
          aria-label="Cancel"
        >
          Cancel
        </Button>
        <Button 
          onClick={onSubmit} 
          variant="contained"
          data-testid="generic-form-submit-button"
          aria-label={submitLabel}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

GenericFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      required: PropTypes.bool,
      recordChoice: PropTypes.oneOf(["type"]),
      fill: PropTypes.shape({ label: PropTypes.string, value: PropTypes.string, broken: PropTypes.bool }),
      options: PropTypes.array,
    })
  ).isRequired,
  formData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitLabel: PropTypes.string,
  formType: PropTypes.string,
};

GenericFormDialog.defaultProps = {
  submitLabel: "Submit",
};

export default GenericFormDialog;
